import React from "react";
import {
  Row, Col, Button, Label, Input, FormFeedback, Form, Spinner, FormGroup
} from "reactstrap";
import withRouter from "components/Common/withRouter";

import { Breadcrumb, FormPhotoUpload } from "../../components/Common";
import { useUserProfile } from "./hooks/useUserProfile";
import { MaskHelper } from "../../utils/helpers";

const convertISOToBrDate = (isoDate) => {
  if (!isoDate) return "";
  const parts = isoDate.split("-");
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return isoDate;
};

const convertBrDateToISO = (brDate) => {
  if (!brDate) return "";
  const parts = brDate.split("/");
  if (parts.length === 3) {
    const day = parts[0];
    const month = parts[1];
    const year = parts[2];
    if (day.length === 2 && month.length === 2 && year.length === 4) {
      return `${year}-${month}-${day}`;
    }
  }
  return "invalid";
};

const UserProfile = () => {
  document.title = "Meu Perfil | NeoApp";

  const { state, validation, actions } = useUserProfile();
  const { initialLoading, saving, cepLoading, authUser, isPersonal } = state;
  const { handleCepChange } = actions;

  const [birthDateInput, setBirthDateInput] = React.useState("");
  const initializedRef = React.useRef(false);

  React.useEffect(() => {
    if (!initialLoading && !initializedRef.current) {
      if (validation.values.birthDate) {
        setBirthDateInput(convertISOToBrDate(validation.values.birthDate));
      }
      initializedRef.current = true;
    }
  }, [initialLoading, validation.values.birthDate]);

  const handleBirthDateChange = (e) => {
    let val = e.target.value;
    val = val.replace(/\D/g, "");
    if (val.length > 8) val = val.substring(0, 8);
    
    const formatted = MaskHelper.date(val);
    setBirthDateInput(formatted);
    
    if (formatted.length === 10) {
      validation.setFieldValue("birthDate", convertBrDateToISO(formatted));
    } else if (formatted.length === 0) {
      validation.setFieldValue("birthDate", "");
    } else {
      validation.setFieldValue("birthDate", "invalid");
    }
  };

  if (initialLoading) {
    return (
      <React.Fragment>
         <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
           <Spinner color="primary" />
         </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <Breadcrumb title="Meu Perfil" />
      </div>

      <div className="border-top mb-3"></div>

      <Form onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
      }}>
          <div className="bg-white p-4 rounded-0 mb-4 border-top">
            <h5 className="mb-4">Informações Pessoais</h5>
            <Row className="mb-4 align-items-center">
              <Col md="auto" className="text-center pe-4 border-end d-none d-md-block">
                <FormPhotoUpload
                  photoPreview={validation.values.photoUrl}
                  onChange={(base64Str) => validation.setFieldValue("photoUrl", base64Str)}
                  size="lg"
                />
                <div className="mt-3 text-muted" style={{ fontSize: '11px', fontWeight: '500' }}>FOTO DO PERFIL</div>
              </Col>
              <Col className="ps-md-4">
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label>Nome Completo (Login: {authUser?.email})</Label>
                      <Row>
                        <Col sm={6}>
                          <Input
                            name="firstName"
                            className={`form-control rounded-0 ${validation.touched.firstName && validation.errors.firstName ? 'is-invalid' : ''}`}
                            placeholder="Nome"
                            type="text"
                            onChange={validation.handleChange}
                            value={validation.values.firstName || ""}
                          />
                          {validation.touched.firstName && validation.errors.firstName ? (
                            <FormFeedback>{validation.errors.firstName}</FormFeedback>
                          ) : null}
                        </Col>
                        <Col sm={6}>
                          <Input
                            name="lastName"
                            className={`form-control rounded-0 mt-3 mt-sm-0 ${validation.touched.lastName && validation.errors.lastName ? 'is-invalid' : ''}`}
                            placeholder="Sobrenome"
                            type="text"
                            onChange={validation.handleChange}
                            value={validation.values.lastName || ""}
                          />
                          {validation.touched.lastName && validation.errors.lastName ? (
                            <FormFeedback>{validation.errors.lastName}</FormFeedback>
                          ) : null}
                        </Col>
                      </Row>
                    </FormGroup>
                  </Col>
                  
                  <Col md={3}>
                    <FormGroup className="mb-3">
                      <Label>Sexo</Label>
                      <Input
                        name="gender"
                        type="select"
                        className="form-select rounded-0"
                        onChange={validation.handleChange}
                        value={validation.values.gender || ""}
                      >
                        <option value="">Selecione</option>
                        <option value="male">Masculino</option>
                        <option value="female">Feminino</option>
                        <option value="other">Outro</option>
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md={3}>
                    <FormGroup className="mb-3">
                      <Label>Acesso</Label>
                      <Input
                        className="form-control rounded-0 bg-light text-muted"
                        type="text"
                        disabled
                        value={isPersonal ? "Plano Personal" : "Proprietário / Admin"}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="d-md-none mt-3">
                  <Col className="text-center">
                    <FormPhotoUpload
                      photoPreview={validation.values.photoUrl}
                      onChange={(base64Str) => validation.setFieldValue("photoUrl", base64Str)}
                      size="xl"
                    />
                    <div className="mt-2 text-muted small fw-semibold">FOTO DO PERFIL</div>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <FormGroup className="mb-3">
                      <Label>Data de Nascimento</Label>
                      <Input
                        name="birthDate"
                        type="text"
                        placeholder="DD/MM/AAAA"
                        className="form-control rounded-0"
                        onChange={handleBirthDateChange}
                        onBlur={() => validation.setFieldTouched("birthDate", true)}
                        value={birthDateInput}
                        invalid={validation.touched.birthDate && !!validation.errors.birthDate}
                      />
                      {validation.touched.birthDate && validation.errors.birthDate && (
                        <FormFeedback>{validation.errors.birthDate}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup className="mb-3">
                      <Label>Telefone / Celular</Label>
                      <Input
                        name="phone"
                        className="form-control rounded-0"
                        placeholder="(00) 00000-0000"
                        type="text"
                        onChange={(e) => validation.setFieldValue("phone", MaskHelper.phone(e.target.value))}
                        value={validation.values.phone || ""}
                      />
                    </FormGroup>
                  </Col>
                  {isPersonal && (
                    <Col md={4}>
                      <FormGroup className="mb-3">
                        <Label>Especialização</Label>
                        <Input
                          name="specialization"
                          className="form-control rounded-0"
                          placeholder="Ex: Musculação Avançada"
                          type="text"
                          onChange={validation.handleChange}
                          value={validation.values.specialization || ""}
                        />
                      </FormGroup>
                    </Col>
                  )}
                </Row>
              </Col>
            </Row>
          </div>

          <div className="bg-white p-4 rounded-0 mt-4 border-top">
            <h5 className="mb-4">Endereço de Contato</h5>
            <Row>
              <Col md={3}>
                <FormGroup className="mb-3 position-relative">
                  <Label>CEP</Label>
                  <Input
                    name="zipCode"
                    className="form-control rounded-0"
                    placeholder="00000-000"
                    type="text"
                    onChange={handleCepChange}
                    value={validation.values.zipCode || ""}
                    maxLength={9}
                  />
                  {cepLoading && (
                    <Spinner size="sm" color="primary" className="position-absolute" style={{ right: '10px', top: '35px' }} />
                  )}
                </FormGroup>
              </Col>
              <Col md={7}>
                <FormGroup className="mb-3">
                  <Label>Logradouro</Label>
                  <Input
                    name="street"
                    className="form-control rounded-0"
                    placeholder="Rua / Avenida"
                    type="text"
                    onChange={validation.handleChange}
                    value={validation.values.street || ""}
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-3">
                  <Label>Número</Label>
                  <Input
                    name="number"
                    className="form-control rounded-0"
                    placeholder="123"
                    type="text"
                    onChange={validation.handleChange}
                    value={validation.values.number || ""}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label>Bairro</Label>
                  <Input
                    name="neighborhood"
                    className="form-control rounded-0"
                    type="text"
                    onChange={validation.handleChange}
                    value={validation.values.neighborhood || ""}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label>Cidade</Label>
                  <Input
                    name="city"
                    className="form-control rounded-0"
                    type="text"
                    onChange={validation.handleChange}
                    value={validation.values.city || ""}
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-3">
                  <Label>UF</Label>
                  <Input
                    name="state"
                    type="select"
                    className="form-select rounded-0"
                    onChange={validation.handleChange}
                    value={validation.values.state || ""}
                  >
                    <option value="">--</option>
                    {["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"].map(uf => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </div>

          <div className="bg-white p-4 rounded-0 mt-4 border-top mb-4">
            <h5 className="mb-4">Segurança da Conta</h5>
            <p className="text-muted font-size-13 mb-4">
               Deixe em branco caso não queira alterar sua senha atual.
            </p>
            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label>Nova Senha</Label>
                  <Input
                    name="password"
                    className={`form-control rounded-0 ${validation.touched.password && validation.errors.password ? 'is-invalid' : ''}`}
                    placeholder="Sua nova senha"
                    type="password"
                    onChange={validation.handleChange}
                    value={validation.values.password || ""}
                  />
                  {validation.touched.password && validation.errors.password ? (
                    <FormFeedback>{validation.errors.password}</FormFeedback>
                  ) : null}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label>Confirmar Nova Senha</Label>
                  <Input
                    name="confirmPassword"
                    className={`form-control rounded-0 ${validation.touched.confirmPassword && validation.errors.confirmPassword ? 'is-invalid' : ''}`}
                    placeholder="Repita sua nova senha"
                    type="password"
                    onChange={validation.handleChange}
                    value={validation.values.confirmPassword || ""}
                  />
                  {validation.touched.confirmPassword && validation.errors.confirmPassword ? (
                    <FormFeedback>{validation.errors.confirmPassword}</FormFeedback>
                  ) : null}
                </FormGroup>
              </Col>
            </Row>
          </div>

          <Row>
             <Col>
                <div className="d-flex flex-wrap gap-2 justify-content-end mb-4">
                  <Button type="submit" color="primary" className="rounded-0 px-4" disabled={saving}>
                    {saving ? (
                      <>
                        <i className="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
                        Salvando...
                      </>
                    ) : (
                      "Salvar Alterações"
                    )}
                  </Button>
                </div>
             </Col>
          </Row>
      </Form>
    </React.Fragment>
  );
};

export default withRouter(UserProfile);
